import React, { useState, useMemo } from 'react';
import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from './Icons';

interface MatrixEditorProps {
  value: string;
  onChange: (json: string) => void;
}

interface MatrixConfig {
  rows: string[];
  columns: string[];
  scores?: Record<string, Record<string, number>>;
}

function parseConfig(value: string): MatrixConfig {
  try {
    const parsed = JSON.parse(value);
    return {
      rows: Array.isArray(parsed.rows) ? parsed.rows : [],
      columns: Array.isArray(parsed.columns) ? parsed.columns : [],
      scores: parsed.scores || undefined,
    };
  } catch {
    return { rows: [], columns: [] };
  }
}

function serialize(config: MatrixConfig): string {
  const out: any = { rows: config.rows, columns: config.columns };
  if (config.scores && Object.keys(config.scores).length > 0) {
    out.scores = config.scores;
  }
  return JSON.stringify(out);
}

const MatrixEditor: React.FC<MatrixEditorProps> = ({ value, onChange }) => {
  const config = useMemo(() => parseConfig(value), [value]);
  const { rows, columns, scores } = config;
  const hasScores = !!scores && Object.keys(scores).length > 0;

  const [scoreEnabled, setScoreEnabled] = useState(hasScores);
  const [showPreview, setShowPreview] = useState(false);
  const [showBulkScore, setShowBulkScore] = useState(false);
  const [bulkScores, setBulkScores] = useState<Record<string, string>>({});
  const [editingColumn, setEditingColumn] = useState<number | null>(null);
  const [editingColumnValue, setEditingColumnValue] = useState('');
  const [newColumnValue, setNewColumnValue] = useState('');

  const emit = (next: MatrixConfig) => onChange(serialize(next));

  // --- Column operations ---
  const addColumn = () => {
    const name = newColumnValue.trim() || `选项${columns.length + 1}`;
    setNewColumnValue('');
    emit({ ...config, columns: [...columns, name] });
  };

  const removeColumn = (idx: number) => {
    const colName = columns[idx];
    const newCols = columns.filter((_, i) => i !== idx);
    let newScores = scores ? { ...scores } : undefined;
    if (newScores) {
      for (const row of Object.keys(newScores)) {
        const rowScores = { ...newScores[row] };
        delete rowScores[colName];
        newScores[row] = rowScores;
      }
    }
    emit({ ...config, columns: newCols, scores: newScores });
  };

  const renameColumn = (idx: number, newName: string) => {
    const oldName = columns[idx];
    if (newName === oldName) return;
    const newCols = [...columns];
    newCols[idx] = newName;
    let newScores = scores ? { ...scores } : undefined;
    if (newScores) {
      for (const row of Object.keys(newScores)) {
        const rowScores = { ...newScores[row] };
        if (oldName in rowScores) {
          rowScores[newName] = rowScores[oldName];
          delete rowScores[oldName];
        }
        newScores[row] = rowScores;
      }
    }
    emit({ ...config, columns: newCols, scores: newScores });
  };

  const startEditColumn = (idx: number) => {
    setEditingColumn(idx);
    setEditingColumnValue(columns[idx]);
  };

  const commitEditColumn = () => {
    if (editingColumn !== null && editingColumnValue.trim()) {
      renameColumn(editingColumn, editingColumnValue.trim());
    }
    setEditingColumn(null);
  };

  // --- Row operations ---
  const addRow = () => {
    const name = `行${rows.length + 1}`;
    emit({ ...config, rows: [...rows, name] });
  };

  const removeRow = (idx: number) => {
    const rowName = rows[idx];
    const newRows = rows.filter((_, i) => i !== idx);
    let newScores = scores ? { ...scores } : undefined;
    if (newScores) {
      delete newScores[rowName];
    }
    emit({ ...config, rows: newRows, scores: newScores });
  };

  const renameRow = (idx: number, newName: string) => {
    const oldName = rows[idx];
    if (newName === oldName) return;
    const newRows = [...rows];
    newRows[idx] = newName;
    let newScores = scores ? { ...scores } : undefined;
    if (newScores && oldName in newScores) {
      newScores[newName] = newScores[oldName];
      delete newScores[oldName];
    }
    emit({ ...config, rows: newRows, scores: newScores });
  };

  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const newRows = [...rows];
    [newRows[idx], newRows[target]] = [newRows[target], newRows[idx]];
    emit({ ...config, rows: newRows });
  };

  // --- Score operations ---
  const toggleScores = (enabled: boolean) => {
    setScoreEnabled(enabled);
    if (!enabled) {
      emit({ ...config, scores: undefined });
    } else {
      // Initialize empty scores
      const newScores: Record<string, Record<string, number>> = {};
      for (const row of rows) {
        newScores[row] = {};
        for (const col of columns) {
          newScores[row][col] = 0;
        }
      }
      emit({ ...config, scores: newScores });
    }
  };

  const setScore = (row: string, col: string, val: number) => {
    const newScores = { ...(scores || {}) };
    if (!newScores[row]) newScores[row] = {};
    newScores[row] = { ...newScores[row], [col]: val };
    emit({ ...config, scores: newScores });
  };

  const applyBulkScores = () => {
    const newScores: Record<string, Record<string, number>> = {};
    for (const row of rows) {
      newScores[row] = {};
      for (const col of columns) {
        const val = parseFloat(bulkScores[col] || '0');
        newScores[row][col] = isNaN(val) ? 0 : val;
      }
    }
    emit({ ...config, scores: newScores });
    setShowBulkScore(false);
  };

  return (
    <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-white">
      <h4 className="text-sm font-semibold text-gray-800 mb-4">矩阵题设置</h4>

      {/* Column management */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-600 mb-2">列选项（表头）</label>
        <div className="flex flex-wrap items-center gap-2">
          {columns.map((col, idx) => (
            <span
              key={idx}
              className="inline-flex items-center bg-primary-50 text-primary-700 text-sm px-3 py-1 rounded-full border border-primary-200"
            >
              {editingColumn === idx ? (
                <input
                  type="text"
                  className="bg-transparent border-none outline-none w-20 text-sm"
                  value={editingColumnValue}
                  onChange={(e) => setEditingColumnValue(e.target.value)}
                  onBlur={commitEditColumn}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEditColumn();
                    if (e.key === 'Escape') setEditingColumn(null);
                  }}
                  autoFocus
                />
              ) : (
                <span
                  className="cursor-pointer"
                  onClick={() => startEditColumn(idx)}
                  title="点击编辑"
                >
                  {col}
                </span>
              )}
              <button
                type="button"
                onClick={() => removeColumn(idx)}
                className="ml-1.5 text-primary-400 hover:text-danger-500 transition-colors"
                title="删除列"
              >
                &times;
              </button>
            </span>
          ))}
          <div className="inline-flex items-center gap-1">
            <input
              type="text"
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-24 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="列名称"
              value={newColumnValue}
              onChange={(e) => setNewColumnValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addColumn(); } }}
            />
            <button
              type="button"
              onClick={addColumn}
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 px-2 py-1 rounded hover:bg-primary-50 transition-colors"
            >
              <PlusIcon size={14} /> 添加列
            </button>
          </div>
        </div>
      </div>

      {/* Score toggle */}
      <div className="mb-4">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={scoreEnabled}
            onChange={(e) => toggleScores(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">启用分数</span>
        </label>
      </div>

      {/* Row list */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-600 mb-2">行列表</label>
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500">第{idx + 1}行</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveRow(idx, -1)}
                    disabled={idx === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="上移"
                  >
                    <ChevronUpIcon size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveRow(idx, 1)}
                    disabled={idx === rows.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="下移"
                  >
                    <ChevronDownIcon size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="p-1 text-gray-400 hover:text-danger-500 transition-colors"
                    title="删除行"
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              </div>
              <div className="mb-2">
                <label className="text-xs text-gray-500 mr-2">行标题:</label>
                <input
                  type="text"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary-500"
                  value={row}
                  onChange={(e) => renameRow(idx, e.target.value)}
                />
              </div>
              {scoreEnabled && columns.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-2">
                  <label className="text-xs text-gray-500 self-center">分数:</label>
                  {columns.map((col, cIdx) => (
                    <div key={cIdx} className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">{col}</span>
                      <input
                        type="number"
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={scores?.[row]?.[col] ?? 0}
                        onChange={(e) => setScore(row, col, parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 px-3 py-1.5 rounded-lg border border-primary-200 hover:bg-primary-50 transition-colors"
          >
            <PlusIcon size={14} /> 添加行
          </button>
          {scoreEnabled && columns.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const initial: Record<string, string> = {};
                columns.forEach((col) => { initial[col] = '0'; });
                setBulkScores(initial);
                setShowBulkScore(true);
              }}
              className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              批量设置分数
            </button>
          )}
        </div>
      </div>

      {/* Bulk score panel */}
      {showBulkScore && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h5 className="text-xs font-medium text-yellow-800 mb-2">批量设置分数（将应用到所有行）</h5>
          <div className="flex flex-wrap gap-3 mb-3">
            {columns.map((col, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <span className="text-xs text-gray-600">{col}</span>
                <input
                  type="number"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                  value={bulkScores[col] || '0'}
                  onChange={(e) => setBulkScores({ ...bulkScores, [col]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyBulkScores}
              className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 transition-colors"
            >
              应用
            </button>
            <button
              type="button"
              onClick={() => setShowBulkScore(false)}
              className="text-sm text-gray-600 px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="border-t border-gray-200 pt-3">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
        >
          <span className={`transform transition-transform ${showPreview ? 'rotate-90' : ''}`}>&#9654;</span>
          预览
        </button>
        {showPreview && rows.length > 0 && columns.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left text-gray-700"></th>
                  {columns.map((col, idx) => (
                    <th key={idx} className="border border-gray-300 px-3 py-2 text-center text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-3 py-2 font-medium text-gray-700">{row}</td>
                    {columns.map((col, cIdx) => (
                      <td key={cIdx} className="border border-gray-300 px-3 py-2 text-center">
                        <span className="text-gray-400">&#9675;</span>
                        {scoreEnabled && scores?.[row]?.[col] !== undefined && (
                          <span className="block text-xs text-gray-400 mt-0.5">
                            {scores[row][col]}分
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showPreview && (rows.length === 0 || columns.length === 0) && (
          <p className="mt-2 text-xs text-gray-400">请先添加行和列</p>
        )}
      </div>
    </div>
  );
};

export default MatrixEditor;
